/**
 * ChartUtilitiesHelper
 * 
 * Static utility methods for chart data processing and formatting.
 * 
 * Pure helper methods that don't modify component state.
 * Main chart orchestration methods remain in component.
 */

import { CrossTabOutputItem, checkboxSelectionWithParent, LineChart, StackChart, GroupBarChart } from 'src/app/common/models/crosstab.model';
import { Status } from 'src/app/common/models/enum/status.enum';

export class ChartUtilitiesHelper {
  
  /**
   * Get share number based on base type and weight selection
   */
  static getShareNumber(
    item: CrossTabOutputItem,
    isTotalBase: boolean,
    selectedWeight: string
  ): number | null {
    return isTotalBase
      ? (selectedWeight === 'Unweighted' ? item.totalUnweightedShare : item.totalWeightedShare)
      : (selectedWeight === 'Unweighted' ? item.answerUnweightedShare : item.answerWeightedShare);
  }

  /**
   * Calculate percentage value from share number
   */
  static calculatePercentageValue(
    shareNumber: number | null,
    item: CrossTabOutputItem
  ): number | string {
    if (shareNumber === null) return 'NA';
    return item.isDerived && item.CalculationType === 'mean'
      ? Number(shareNumber.toFixed(1))
      : Number((shareNumber * 100).toFixed(1));
  }

  /**
   * Get change vs previous year value
   */
  static getChangeVsPY(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): number | string {
    const changevsPY = isTotalBase ? item.changeVsPY_Total : item.changeVsPY_Answer;
    if (changevsPY === null) return 'NA';
    return item.isDerived && item.CalculationType === 'mean'
      ? Number(changevsPY.toFixed(1))
      : Number((changevsPY * 100).toFixed(1));
  }

  /**
   * Get significance value as string
   */
  static getSignificanceString(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): string {
    const sig = isTotalBase ? item.Significance_Total : item.Significance_Answer;
    return sig === null ? '0' : sig.toString();
  }

  /**
   * Get sample size value as string
   */
  static getSampleSizeString(
    item: CrossTabOutputItem,
    isTotalBase: boolean
  ): string {
    const base = isTotalBase ? item.totalUnweightedBase : item.answerUnweightedBase;
    return base === null ? '0' : base.toString();
  }

  /**
   * Get distinct formatted child values from data object
   */
  static getDistinctChildValuesFormatted(
    dataObject: checkboxSelectionWithParent[]
  ): string[] {
    return Array.from(
      new Set(dataObject.flatMap(parent => parent.Data.map(selection => selection.Child)))
    ).map(child => child.replaceAll('|', ' : '));
  }

  /**
   * Filter data by calculation type
   */
  static filterDataByCalculationType(
    data: checkboxSelectionWithParent[],
    type: 'multipunch' | 'respgrp' | 'mean'
  ): checkboxSelectionWithParent[] {
    return data.filter(parent => {
      return parent.Data.some(child => {
        switch (type) {
          case 'multipunch': return child.isMultiPunch === Status.MultiPunchDisableChart;
          case 'respgrp': return child.CalculationType === 'RespGrp' && child.isDerviedVariable === true;
          case 'mean': return child.CalculationType.toLowerCase() === 'mean' && child.isDerviedVariable === true;
        }
      });
    });
  }

  /**
   * Initialize chart data and properties arrays
   */
  static initializeChartData(
    chart: LineChart | StackChart | GroupBarChart
  ): void {
    chart.Data = [];
    chart.Properties = [];
  }

  /**
   * Build common chart properties object
   */
  static buildCommonChartProperties(
    distinctColChildValues: string[],
    numberType: string,
    chartStyle: string = '',
    DefaultSampleSize: string = 'None',
    CrossTabOutPutData: any[] = []
  ): any {
    return {
      plotBands: false,
      lowSample: false,
      showNegative: false,
      allowOverlap: chartStyle === 'area' ? false : true,
      chartStyle: chartStyle,
      showLegend: true,
      gridlines: false,
      shadow: false,
      stacking: 0,
      darkTheme: false,
      legendPosition: 'bottom',
      axisLabelOrientation: chartStyle === 'area' ? 'center' : 'horizontal',
      chartTitlePosition: 'center',
      showDataLabel: true,
      datalabelStyle: DefaultSampleSize === 'None' ? (chartStyle === 'area' ? '' : 'datalabel') : (chartStyle === 'area' ? 'sigchan' : 'change'),
      axisImage: false,
      dataLabelPosition: 'center',
      dataLabelColor: 'black',
      marker: 'disable',
      showRemaining: false,
      gradientBarColor: false,
      showPattern: false,
      dataLabelPositionPPT: chartStyle === 'area' ? 'center' : (chartStyle === '' ? 'Center' : 'Inside Base'),
      datalabelFormat: numberType === CrossTabOutPutData?.[0]?.rowChild?.toLowerCase().includes('mean') ? (chartStyle === 'area' ? 'number' : '') : 'percentage',
      datalabelOrientation: 0,
      lineWidth: chartStyle === 'area' ? 2 : 0,
      gridLineDashStyle: chartStyle === 'area' ? 'Solid' : '',
      markerLineWidth: chartStyle === 'area' ? 1 : 0,
      markerStyle: 'circle',
      title: chartStyle === 'area' ? 'LineChart' : '',
      toolTipStyle: 'default',
      ChartCat: distinctColChildValues
    };
  }

  /**
   * Sort chart data by series name order
   */
  static sortChartDataBySeriesName(
    chartData: any[],
    distinctRowChildValues: string[]
  ): void {
    chartData.sort((a, b) => {
      const indexA = distinctRowChildValues.findIndex(item => item === a['Series Name']);
      const indexB = distinctRowChildValues.findIndex(item => item === b['Series Name']);
      return indexA - indexB;
    });
  }

  /**
   * Get distinct values by pipe count
   */
  static getDistinctValuesByPipeCount(
    data: string[],
    pipeCount: number
  ): string[] {
    const distinctValues = new Set<string>();
    data.forEach((item) => {
      const total = (item.match(/\|/g) || []).length;
      if (pipeCount <= total) {
        const parts = item.split('|')[pipeCount];
        distinctValues.add(parts.trim());
      }
    });
    return Array.from(distinctValues);
  }

  /**
   * Get distinct pipe parts from row values
   */
  static getDistinctPipeParts(
    rowValues: string[]
  ): { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] } {
    return {
      first: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 0),
      second: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 1),
      third: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 2),
      fourth: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 3),
      fifth: ChartUtilitiesHelper.getDistinctValuesByPipeCount(rowValues, 4)
    };
  }

  /**
   * Calculate if stack chart should be enabled
   */
  static calculateStackFlag(
    parts: { first: string[], second: string[], third: string[], fourth: string[], fifth: string[] }
  ): boolean {
    return (parts.fifth.length > 0 && parts.fourth.length == 1 && parts.third.length == 1 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.fourth.length > 0 && parts.third.length == 1 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.third.length > 0 && parts.second.length == 1 && parts.first.length == 1) ||
      (parts.second.length > 0 && parts.first.length == 1);
  }

  /**
   * Get distinct column child values
   */
  static getDistinctColumnChildValues(
    dataObject: { Column: checkboxSelectionWithParent[] }
  ): string[] {
    const distinctColumnChildren = Array.from(new Set(dataObject.Column.flatMap((parent) => parent.Data.map((selection) => selection.Child))));
    return distinctColumnChildren.map((child) => child.replaceAll('|', ' : '));
  }

  /**
   * Filter variable data by pipe levels
   */
  static filterByPipeLevels(
    child: string,
    variableTabData: any[]
  ): any[] {
    const levels = child.split('|');
    const pipeCount = levels.length - 1;

    return variableTabData.filter((x: any) => {
      if (x.Header !== 'VariableList') return false;

      const levelChecks = [
        () => x.Level1 === levels[0],
        () => x.Level2 === levels[1],
        () => x.level3 === levels[2],
        () => x.level4 === levels[3],
        () => x.level5 === levels[4]
      ];

      return levelChecks.slice(0, pipeCount + 1).every(check => check());
    });
  }

  /**
   * Get distinct child values from data array
   */
  static getDistinctChildValues(
    data: checkboxSelectionWithParent[]
  ): string[] {
    const distinctChildValues: string[] = [];
    data.forEach((item) => {
      item.Data.forEach((checkbox) => {
        if (!distinctChildValues.includes(checkbox.Child)) {
          distinctChildValues.push(checkbox.Child);
        }
      });
    });

    return distinctChildValues;
  }

  static getFormattedCustomProperties(props: any, chartType: string): any {
    const isTable = chartType && chartType.toLowerCase().includes('table');
    const isFunnel = chartType && chartType.toLowerCase().includes('funnel');

    const baseDefaults: any = {
      hideNA: true,
      datalabelProperty: 'default',
      datalabelStyle: 'default',
      datalabelFormat: 'percentage',
      decimalRoundOff: isFunnel ? 2 : 0,
      borderStyle: '1',
      borderColor: '#CAC4C4',
      ColumnHeaderAlignment: 'center',
      dataLabelAlignment: 'center',
      CalculationLogic: 'None',
      enableDataBars: true,
      ColumnHeaderSorting: true,
      legendPosition: 'bottom'
    };

    if (!props) return JSON.stringify(baseDefaults);

    let parsedProps: any;
    if (typeof props === 'string') {
      try {
        parsedProps = JSON.parse(props);
      } catch (e) {
        parsedProps = {};
      }
    } else {
      parsedProps = props;
    }

    if (!parsedProps || typeof parsedProps !== 'object') {
      parsedProps = {};
    }

    Object.keys(baseDefaults).forEach(key => {
      if (parsedProps[key] === undefined) {
        parsedProps[key] = baseDefaults[key];
      }
    });

    if (isFunnel && (parsedProps.decimalRoundOff === undefined || parsedProps.decimalRoundOff === 0)) {
      parsedProps.decimalRoundOff = 2;
    }

    const finalStr = JSON.stringify(parsedProps);
    return finalStr;
  }

  static isChartType(comp: any, block: any, chartType: string): boolean {
    if (block.isVerticalGroupBarChartClick) return chartType === 'groupBar';
    if (block.isVerticalBarChartClick) return chartType === 'bar';
    if (block.isVerticalStackChartClick) return chartType === 'stack';
    if (block.isLineChartClick) return chartType === 'line';

    const type = block.fullAnswer?.AgentPayload?.chartType?.toLowerCase() ||
      block.analyticalData?.AgentPayload?.chartType?.toLowerCase();

    if (!type && comp.getMcode(block) === 1) return chartType === 'groupBar';

    switch (chartType) {
      case 'groupBar': return type === 'groupbar' || type === 'column' || !type;
      case 'bar': return type === 'bar';
      case 'stack': return type === 'stack' || type === 'stackedbar';
      case 'line': return type === 'line' || type === 'trend';
    }
    return false;
  }

  static calcThershold(comp: any, type: string): string {
    if (comp.threshold === null || comp.threshold === undefined || comp.threshold === '') {
      return '';
    }
    const thresholdValue = Number(comp.threshold) || 0;
    if (type === 'Add') {
      return (100 + thresholdValue).toString();
    } else {
      return (100 - thresholdValue).toString();
    }
  }

  static neutralizeChartIds(comp: any, block: any = null): void {
    const ids = [
      'line-chartContainer',
      'stack-chart-container',
      'chart-container',
      'vertical-chart-container',
      'stack-chart-container2'
    ];
    const target = block || comp;
    const currentBlockEl = target.messageId ? document.querySelector(`[data-message-id="${target.messageId}"]`) : null;

    if (currentBlockEl) {
      ids.forEach((id: string) => {
        const elements = currentBlockEl.querySelectorAll(`[id^="${id}_rendered_"], #${id}`);
        elements.forEach(el => {
          el.id = id;
        });
      });
    }

    ids.forEach((id: string) => {
      document.querySelectorAll(`#${id}`).forEach(el => {
        if (currentBlockEl && currentBlockEl.contains(el)) {
          return;
        }
        el.id = id + '_rendered_' + Math.random().toString(36).substr(2, 9);
      });
    });
  }

  static applyChartStates(comp: any, activeChart?: string, block: any = null): void {
    const target = block || comp;
    target.isTableClick = activeChart === 'table';
    target.isLineChartClick = activeChart === 'line';
    target.isVerticalBarChartClick = activeChart === 'bar';
    target.isVerticalStackChartClick = activeChart === 'stack';
    target.isVerticalGroupBarChartClick = activeChart === 'groupBar';
    target.isChartView = !!activeChart;

    if (block) {
      if (!block.LineChart) block.LineChart = JSON.parse(JSON.stringify(comp.LineChart));
      if (!block.StackChart) block.StackChart = JSON.parse(JSON.stringify(comp.StackChart));
      if (!block.BarChart) block.BarChart = JSON.parse(JSON.stringify(comp.BarChart));
    }
  }

  static resetChartStates(comp: any, activeChart?: 'table' | 'line' | 'bar' | 'stack' | 'groupBar', block: any = null): void {
    const target = block || comp;
    const isSwitchingFromTable = target.displayTable && activeChart !== 'table' && activeChart !== undefined;
    const isSwitchingToTable = activeChart === 'table' && !target.displayTable;

    if (isSwitchingFromTable) {
      target.isTableClosing = true;
      ChartUtilitiesHelper.applyChartStates(comp, activeChart, block);
      setTimeout(() => {
        target.displayTable = false;
        target.isTableClosing = false;
      }, 500);
    } else if (isSwitchingToTable) {
      target.isTableClick = true;
      target.isChartClosing = true;
      target.displayTable = true;
      ChartUtilitiesHelper.applyChartStates(comp, activeChart, block);

      setTimeout(() => {
        target.isChartClosing = false;
      }, 800);
    } else {
      ChartUtilitiesHelper.applyChartStates(comp, activeChart, block);
    }
  }
}
